// MAS 精靈 API client — 對應 docs/MAS_GENIE_API.md v1.0
// 統一處理 Bearer token + 錯誤格式 + 401 統一攔截

const BASE = "/api/mas-genie";

const buildHeaders = (token) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
});

// 401 全域處理 — 由 UI 層在啟動時用 setUnauthorizedHandler 註冊
// 通常做法：清 auth context + navigate 到 /authentication/sign-in
let unauthorizedHandler = null;
export function setUnauthorizedHandler(fn) {
  unauthorizedHandler = typeof fn === 'function' ? fn : null;
}

async function handleResponse(res) {
  if (!res.ok) {
    let body = {};
    try { body = await res.json(); } catch (_) {}
    const err = new Error(body.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = body.code;
    err.body = body;
    // 401 → 觸發已註冊的 handler（清 token + navigate sign-in）
    if (res.status === 401 && unauthorizedHandler) {
      try { unauthorizedHandler(err); } catch (_) {}
    }
    throw err;
  }
  return res.json();
}

// =============================================================
// 階段 1：許願 + 抽 3 張卡（POST /wish）— v2 主流程
//   後端會扣月配額、回傳已選好的 3 張卡（含 moveKey）
//   idempotency：同 wishToken 重送 → 後端不扣配額、回原本 3 張卡 + idempotentReplay: true
// =============================================================

export async function wishStrategy(token, { ambition, wishToken }) {
  const res = await fetch(`${BASE}/wish`, {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify({ ambition, wishToken }),
  });
  return handleResponse(res);
}

// 產一個 wishToken（uuid v4）— 用 crypto.randomUUID 不用裝 lib
export function generateWishToken() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // 萬一 crypto.randomUUID 不可用（極舊 browser）退回時間戳+亂數
  return `wish-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// =============================================================
// 階段 1b：自由瀏覽策略池（給策略瀏覽頁用，不扣配額）
// =============================================================

export async function fetchElites(token, params = {}) {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null && v !== "")
  );
  const qs = new URLSearchParams(clean).toString();
  const res = await fetch(`${BASE}/elites${qs ? `?${qs}` : ""}`, {
    headers: buildHeaders(token),
  });
  return handleResponse(res);
}

export async function fetchEliteById(token, id) {
  const res = await fetch(`${BASE}/elites/${id}`, { headers: buildHeaders(token) });
  return handleResponse(res);
}

export async function fetchFilterOptions(token) {
  const res = await fetch(`${BASE}/meta/filter-options`, { headers: buildHeaders(token) });
  return handleResponse(res);
}

// =============================================================
// 階段 2：我的精靈策略
// =============================================================

export async function fetchMyStrategies(token, status) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  const res = await fetch(`${BASE}/my-strategies${qs}`, { headers: buildHeaders(token) });
  return handleResponse(res);
}

export async function pickStrategy(token, body) {
  const res = await fetch(`${BASE}/my-strategies`, {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

export async function updateMyStrategy(token, id, body) {
  const res = await fetch(`${BASE}/my-strategies/${id}`, {
    method: "PATCH",
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

// 標記已看過「如何開始交易」新手導引（跨裝置只看一次）；失敗不影響使用，由呼叫端 catch
export async function markGenieGuideSeen(token) {
  const res = await fetch(`${BASE}/guide-seen`, {
    method: "POST",
    headers: buildHeaders(token),
  });
  return handleResponse(res);
}

export async function deleteMyStrategy(token, id) {
  const res = await fetch(`${BASE}/my-strategies/${id}`, {
    method: "DELETE",
    headers: buildHeaders(token),
  });
  return handleResponse(res);
}

// =============================================================
// 階段 3：下載 .exe
// =============================================================

export async function getDownloadStatus(token, id) {
  const res = await fetch(`${BASE}/my-strategies/${id}/download-status`, {
    headers: buildHeaders(token),
  });
  return handleResponse(res);
}

// 用 fetch + blob 才能帶 Authorization header（不能用 window.location.href）
//   onProgress?: ({ loaded, total, percent }) => void
//     percent 為 0–100；伺服器沒給 Content-Length 時 percent = null（前端顯示不定量）
export async function downloadStrategyExe(token, id, filename = "strategy.exe", onProgress) {
  const res = await fetch(`${BASE}/my-strategies/${id}/download`, {
    headers: buildHeaders(token),
  });
  if (!res.ok) {
    let body = {};
    try { body = await res.json(); } catch (_) {}
    const err = new Error(body.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = body.code;
    if (res.status === 401 && unauthorizedHandler) {
      try { unauthorizedHandler(err); } catch (_) {}
    }
    throw err;
  }
  // 進度回報：優先用 stream reader 邊收邊算百分比；
  // 拿不到 reader 時退回 res.blob()（無進度）
  let blob;
  const total = Number(res.headers.get("Content-Length")) || 0;
  if (typeof onProgress === "function" && res.body && typeof res.body.getReader === "function") {
    const reader = res.body.getReader();
    const chunks = [];
    let loaded = 0;
    onProgress({ loaded: 0, total, percent: total ? 0 : null });
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.length;
      onProgress({ loaded, total, percent: total ? Math.min(100, Math.round((loaded / total) * 100)) : null });
    }
    blob = new Blob(chunks);
  } else {
    blob = await res.blob();
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
